import { useCallback } from "react"
import { useCore } from "@app/hooks/useCore"
import { useShopify } from "@app/hooks/useShopify"
import { useTrackingContext } from "@app/providers/tracking"
import { useCustomerContext } from "@app/providers/customer"
import { useCartContext } from "@app/providers/cart"
import { isTrackingAllowed, generateEventId } from "@app/utils/tracking"

import type { CartLine } from "@shopify/hydrogen-react/storefront-api-types"

/**
 * Tracking hook. Single source of truth for ecommerce / customer events.
 *
 * Every event is pushed to window.ElevarDataLayer. The GTM container
 * (GTM-MLH72QV, Elevar 2.0 setup) consumes dl_* events and fans out to
 * Klaviyo, GA4, Facebook, TikTok and Google Ads via dedicated tags.
 *
 * The previous parallel pushDataLayer (GA-shaped event names without the
 * dl_ prefix) pipeline was confirmed dead — no GTM trigger consumed those
 * events — and was removed in RE-56718 Phase 2.
 *
 * pushElevarDataLayer is gated via isTrackingAllowed() — the policy lives in
 * @app/utils/tracking. Each push also gets a unique event_id for FB / TikTok
 * server-side dedupe.
 */
export const useAnalytics = () => {
  const { tracked } = useTrackingContext()
  const {
    helpers: { decodeShopifyId, formatPrice, isBrowser },
  } = useCore()
  const { cart, currencyCode } = useCartContext()
  const { customer } = useCustomerContext()
  const { productNormaliser, variantNormaliser } = useShopify()

  // GA Linker URL decoration for the Shopify checkout handoff. Pure URL
  // utility, unrelated to the dataLayer event pipeline.
  const decorateUrl = useCallback(
    (url: string): string => {
      if (isBrowser) {
        // @ts-ignore next-line
        const ga = window[window["GoogleAnalyticsObject"]]
        // @ts-ignore next-line
        if (ga && typeof ga.getAll === "function") {
          // @ts-ignore next-line
          const tracker = ga.getAll()[0]
          // @ts-ignore next-line
          url = new window.gaplugins.Linker(tracker).decorate(url)
        }
        return url
      } else {
        return url
      }
    },
    [isBrowser]
  )

  const pushElevarDataLayer = useCallback((dataLayer: any) => {
    if (!isTrackingAllowed()) return
    window.ElevarDataLayer = window.ElevarDataLayer ?? []
    window.ElevarDataLayer.push({ ...dataLayer, event_id: dataLayer?.event_id ?? generateEventId() })
  }, [])

  const trackUserData = useCallback(() => {
    pushElevarDataLayer({
      event: "dl_user_data",
      cart_total: cart?.cost?.subtotalAmount?.amount ?? "0.00",
      // Only set marketing.user_id when we have an authenticated customer id.
      // For guests, leave the key alone so Elevar's anonymous user_id (pushed
      // by Elevar's own context entries) persists in the dataLayer state.
      ...(customer?.id && { marketing: { user_id: customer.id } }),
      user_properties: {
        customer_id: customer?.id,
        customer_email: customer?.email,
        visitor_type: customer?.id ? "logged_in" : "guest",
      },
      ecommerce: {
        currencyCode,
        cart_contents: {
          products:
            cart?.lines?.map(lineitem => {
              const imageSrc = lineitem?.merchandise?.image?.originalSrc
              return {
                id: decodeShopifyId(lineitem?.merchandise?.id, "ProductVariant"),
                name: lineitem?.merchandise?.product?.title || "N/A",
                brand: lineitem?.merchandise?.product?.vendor || "N/A",
                category: lineitem?.merchandise?.product?.productType || "N/A",
                variant: lineitem?.merchandise?.title || "N/A",
                price: formatPrice(lineitem?.merchandise?.priceV2?.amount),
                compare_at_price:
                  formatPrice(lineitem?.merchandise?.compareAtPriceV2?.amount) === "NaN"
                    ? formatPrice(lineitem?.merchandise?.priceV2?.amount)
                    : formatPrice(lineitem?.merchandise?.compareAtPriceV2?.amount),
                quantity: lineitem?.quantity.toString(),
                product_id: decodeShopifyId(lineitem?.merchandise?.product?.id, "Product"),
                variant_id: decodeShopifyId(lineitem?.merchandise?.id, "ProductVariant"),
                list: "N/A",
                ...(imageSrc && { image: imageSrc }),
              }
            }) ?? [],
        },
      },
    })
  }, [
    cart?.cost?.subtotalAmount?.amount,
    cart?.lines,
    currencyCode,
    customer?.email,
    customer?.id,
    decodeShopifyId,
    formatPrice,
    pushElevarDataLayer,
  ])

  const trackElevarPageView = useCallback(() => {
    window.ElevarInvalidateContext?.()
    setTimeout(() => {
      trackUserData()
    }, 0)
  }, [trackUserData])

  // No-op stub — Layout still imports this. Page-view signals flow through
  // dl_user_data via trackElevarPageView. The previous GA-shaped page_view
  // push had no GTM consumer.
  const trackPageView = useCallback(() => {}, [])

  // No-op stub. The previous GA-shaped per-card view_item_list push had no
  // GTM consumer. For collection-wide list views use trackCollectionView.
  const trackProductImpression = useCallback(async (..._args: any[]) => {}, [])

  const trackProductView = useCallback(
    async (product: any, variant: any, parentCollection?: string | false) => {
      const { id, productType, title, vendor } = productNormaliser(product)
      if (!title) return
      // Variant shape differs between initial PDP load (priceV2 sometimes
      // pre-flattened to a string) and variant-change (raw Storefront API
      // MoneyV2 object {amount, currencyCode}). Defensive extraction +
      // String cast handles both safely without breaking formatPrice.
      const priceRaw = variant?.priceV2?.amount ?? variant?.priceV2
      const compareRaw = variant?.compareAtPriceV2?.amount ?? variant?.compareAtPriceV2
      const priceFmt = priceRaw != null ? formatPrice(String(priceRaw)) : ""
      const compareFmt = compareRaw != null ? formatPrice(String(compareRaw)) : "NaN"
      const imageSrc = variant?.image?.src || product?.featuredImage?.originalSrc
      pushElevarDataLayer({
        event: "dl_view_item",
        ecommerce: {
          currencyCode,
          detail: {
            actionField: { list: parentCollection || "N/A", action: "detail" },
            products: [
              {
                id: decodeShopifyId(id, "Product"),
                name: title || "N/A",
                brand: vendor || "N/A",
                category: productType || "N/A",
                variant: variant?.title || "N/A",
                price: priceFmt,
                compare_at_price: compareFmt === "NaN" ? priceFmt : compareFmt,
                list: parentCollection || "N/A",
                product_id: decodeShopifyId(id, "Product"),
                variant_id: decodeShopifyId(variant?.id, "ProductVariant"),
                ...(imageSrc && { image: imageSrc }),
              },
            ],
          },
        },
      })
    },
    [productNormaliser, currencyCode, decodeShopifyId, formatPrice, pushElevarDataLayer]
  )

  const trackProductClick = useCallback(
    async (product: any, variant: any, index: number, list: string | null = null) => {
      const { id, title, vendor, productType } = product
      if (!title) return
      const imageSrc = variant?.image?.src || product?.featuredImage?.originalSrc
      pushElevarDataLayer({
        event: "dl_select_item",
        ecommerce: {
          currencyCode,
          click: {
            actionField: { list: list || "N/A", action: "click" },
            products: [
              {
                id: decodeShopifyId(id, "Product"),
                name: title || "N/A",
                brand: vendor || "N/A",
                category: productType || "N/A",
                variant: variant?.title || "N/A",
                price: formatPrice(variant?.priceV2?.amount),
                compare_at_price:
                  formatPrice(variant?.compareAtPriceV2?.amount) === "NaN"
                    ? formatPrice(variant?.priceV2?.amount)
                    : formatPrice(variant?.compareAtPriceV2?.amount),
                quantity: "1",
                position: index + 1,
                list: list || "N/A",
                product_id: decodeShopifyId(id, "Product"),
                variant_id: decodeShopifyId(variant?.id, "ProductVariant"),
                ...(imageSrc && { image: imageSrc }),
                url: typeof window !== "undefined" ? window.location.href : "",
              },
            ],
          },
        },
      })
    },
    [currencyCode, decodeShopifyId, formatPrice, pushElevarDataLayer]
  )

  const trackCollectionView = useCallback(
    async (products: any[], listName: string) => {
      if (!products?.length) return
      pushElevarDataLayer({
        event: "dl_view_item_list",
        ecommerce: {
          currencyCode,
          actionField: { list: listName || "N/A" },
          impressions: products.map((product, index) => {
            const normalised = productNormaliser(product)
            const variant = normalised.variants?.[0]
            // Algolia-sourced products return prices as numbers (not Storefront-API
            // strings), so cast to String before formatPrice — formatPrice calls
            // .replace() and throws on number input.
            const rawPrice = variant?.priceV2?.amount ?? normalised.priceRange?.minVariantPrice?.amount
            return {
              id: decodeShopifyId(normalised.id, "Product"),
              name: normalised.title || "N/A",
              brand: normalised.vendor || "N/A",
              category: normalised.productType || normalised.collections?.[0]?.title || "N/A",
              variant: variant?.title || "Default Title",
              price: rawPrice != null ? formatPrice(String(rawPrice)) : "",
              list: listName || "N/A",
              position: index + 1,
              product_id: decodeShopifyId(normalised.id, "Product"),
              variant_id: decodeShopifyId(variant?.id, "ProductVariant"),
            }
          }),
        },
      })
    },
    [productNormaliser, decodeShopifyId, formatPrice, currencyCode, pushElevarDataLayer]
  )

  const trackSearchResults = useCallback(
    async (products: any[], searchTerm: string) => {
      pushElevarDataLayer({
        event: "dl_view_search_results",
        ecommerce: {
          currencyCode,
          actionField: { list: "search results", search_term: searchTerm || "" },
          impressions: (products || []).map((product, index) => {
            const normalised = productNormaliser(product)
            const variant = normalised.variants?.[0]
            const rawPrice = variant?.priceV2?.amount ?? normalised.priceRange?.minVariantPrice?.amount
            return {
              id: decodeShopifyId(normalised.id, "Product"),
              name: normalised.title || "N/A",
              brand: normalised.vendor || "N/A",
              category: normalised.productType || normalised.collections?.[0]?.title || "N/A",
              variant: variant?.title || "Default Title",
              price: rawPrice != null ? formatPrice(String(rawPrice)) : "",
              list: "search results",
              position: index + 1,
              product_id: decodeShopifyId(normalised.id, "Product"),
              variant_id: decodeShopifyId(variant?.id, "ProductVariant"),
            }
          }),
        },
      })
    },
    [productNormaliser, decodeShopifyId, formatPrice, currencyCode, pushElevarDataLayer]
  )

  const trackCartUpdate = useCallback(
    async (type: "add" | "change" | "remove", variantId: string, quantity: number, lineitems: CartLine[]) => {
      const selectedLineItem = lineitems?.filter(line => line?.merchandise?.id === variantId)[0]
      const title = selectedLineItem?.merchandise?.product?.title
      if (!title) return
      const product = productNormaliser(selectedLineItem?.merchandise?.product)
      const { id, vendor } = productNormaliser(selectedLineItem?.merchandise?.product)
      const variant = variantNormaliser(selectedLineItem?.merchandise)
      // Note: dropped the `url` field here. It previously used
      // window.location.href, which captured the user's current page rather
      // than the product's canonical URL — wrong when bumping quantity from
      // the cart drawer or any page other than the PDP. No GTM tag in
      // GTM-MLH72QV currently reads `ecommerce.add.products[0].url`.
      const imageSrc = variant?.image?.src || product?.featuredImage?.originalSrc
      const products = [
        {
          id: decodeShopifyId(id, "Product"),
          name: title || "N/A",
          brand: vendor || "N/A",
          category: selectedLineItem?.merchandise?.product?.productType || "N/A",
          variant: variant?.title || "N/A",
          price: formatPrice(variant?.priceV2?.amount),
          compare_at_price:
            formatPrice(variant?.compareAtPriceV2?.amount) === "NaN"
              ? formatPrice(variant?.priceV2?.amount)
              : formatPrice(variant?.compareAtPriceV2?.amount),
          quantity: quantity.toString(),
          list: "N/A",
          product_id: decodeShopifyId(product.id, "Product"),
          variant_id: decodeShopifyId(variant?.id, "ProductVariant"),
          ...(imageSrc && { image: imageSrc }),
        },
      ]
      const isAdd = type === "add" || type === "change"
      pushElevarDataLayer({
        event: isAdd ? "dl_add_to_cart" : "dl_remove_from_cart",
        ecommerce: {
          currencyCode,
          ...(isAdd ? { add: { actionField: { list: "N/A" }, products } } : { remove: { actionField: { list: "N/A" }, products } }),
        },
      })
    },
    [productNormaliser, variantNormaliser, decodeShopifyId, formatPrice, currencyCode, pushElevarDataLayer]
  )

  const trackCartView = useCallback(async () => {
    if (!cart?.lines?.length) return
    pushElevarDataLayer({
      event: "dl_view_cart",
      cart_total: cart?.cost?.subtotalAmount?.amount ?? "0.00",
      ecommerce: {
        currencyCode,
        actionField: { list: "Shopping Cart" },
        impressions: cart?.lines?.map((lineitem, index) => ({
          id: decodeShopifyId(lineitem?.merchandise?.id, "ProductVariant"),
          name: lineitem?.merchandise?.product?.title || "N/A",
          brand: lineitem?.merchandise?.product?.vendor || "N/A",
          category: lineitem?.merchandise?.product?.productType || "N/A",
          variant: lineitem?.merchandise?.title || "N/A",
          price: formatPrice(lineitem?.merchandise?.priceV2?.amount),
          compare_at_price:
            formatPrice(lineitem?.merchandise?.compareAtPriceV2?.amount) === "NaN"
              ? formatPrice(lineitem?.merchandise?.priceV2?.amount)
              : formatPrice(lineitem?.merchandise?.compareAtPriceV2?.amount),
          quantity: lineitem?.quantity.toString(),
          list: "Shopping Cart",
          product_id: decodeShopifyId(lineitem?.merchandise?.product?.id, "Product"),
          variant_id: decodeShopifyId(lineitem?.merchandise?.id, "ProductVariant"),
          position: index + 1,
        })),
      },
    })
  }, [cart?.lines, cart?.cost?.subtotalAmount?.amount, currencyCode, pushElevarDataLayer, decodeShopifyId, formatPrice])

  const trackBeginCheckout = useCallback(async () => {
    if (!cart?.lines?.length) return
    pushElevarDataLayer({
      event: "dl_begin_checkout",
      cart_total: cart?.cost?.subtotalAmount?.amount ?? "0.00",
      ecommerce: {
        currencyCode,
        checkout: {
          actionField: { step: "1" },
          products: cart?.lines?.map(lineitem => ({
            id: decodeShopifyId(lineitem?.merchandise?.id, "ProductVariant"),
            name: lineitem?.merchandise?.product?.title || "N/A",
            brand: lineitem?.merchandise?.product?.vendor || "N/A",
            category: lineitem?.merchandise?.product?.productType || "N/A",
            variant: lineitem?.merchandise?.title || "N/A",
            price: formatPrice(lineitem?.merchandise?.priceV2?.amount),
            quantity: lineitem?.quantity.toString(),
            product_id: decodeShopifyId(lineitem?.merchandise?.product?.id, "Product"),
            variant_id: decodeShopifyId(lineitem?.merchandise?.id, "ProductVariant"),
            list: "Shopping Cart",
          })),
        },
      },
    })
  }, [cart?.lines, cart?.cost?.subtotalAmount?.amount, currencyCode, pushElevarDataLayer, decodeShopifyId, formatPrice])

  const trackSubscribe = useCallback(
    (leadType: "email" | "phone", email?: string, phone?: string) => {
      pushElevarDataLayer({
        event: "dl_subscribe",
        lead_type: leadType,
        user_properties: {
          ...(email && { customer_email: email }),
          ...(phone && { customer_phone: phone }),
        },
      })
    },
    [pushElevarDataLayer]
  )

  const trackLogin = useCallback(
    (method?: string, customerOverride?: { id?: string; email?: string }) => {
      const id = customerOverride?.id ?? customer?.id
      const email = customerOverride?.email ?? customer?.email
      pushElevarDataLayer({
        event: "dl_login",
        method: method || "shopify",
        ...(id && { marketing: { user_id: id } }),
        user_properties: {
          ...(id && { customer_id: id }),
          ...(email && { customer_email: email }),
          visitor_type: "logged_in",
        },
      })
    },
    [customer?.id, customer?.email, pushElevarDataLayer]
  )

  const trackSignup = useCallback(
    (method?: string, customerOverride?: { id?: string; email?: string }) => {
      const id = customerOverride?.id ?? customer?.id
      const email = customerOverride?.email ?? customer?.email
      pushElevarDataLayer({
        event: "dl_sign_up",
        method: method || "shopify",
        ...(id && { marketing: { user_id: id } }),
        user_properties: {
          ...(id && { customer_id: id }),
          ...(email && { customer_email: email }),
          visitor_type: "logged_in",
        },
      })
    },
    [customer?.id, customer?.email, pushElevarDataLayer]
  )

  // No-op stubs preserved for existing consumers (useSection.ts and wishlist
  // UI). The previous GA-shaped pushes had no GTM consumer in the current
  // container. Remove these in a follow-up once their call sites are also
  // cleaned up or GTM triggers are added.
  const trackWishListUpdate = useCallback(async (..._args: any[]) => {}, [])
  const trackPromoImpression = useCallback(async (..._args: any[]) => {}, [])
  const trackPromoClick = useCallback(async (..._args: any[]) => {}, [])
  const trackShare = useCallback(async (..._args: any[]) => {}, [])
  const trackClick = useCallback(async (..._args: any[]) => {}, [])
  const trackSearch = useCallback(async (..._args: any[]) => {}, [])

  return {
    tracked,
    trackPageView,
    trackElevarPageView,
    trackProductImpression,
    trackProductView,
    trackProductClick,
    trackCollectionView,
    trackSearchResults,
    trackCartView,
    trackCartUpdate,
    trackBeginCheckout,
    trackSubscribe,
    trackWishListUpdate,
    trackPromoImpression,
    trackPromoClick,
    trackLogin,
    trackSignup,
    trackShare,
    trackSearch,
    trackClick,
    decorateUrl,
  }
}
